import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { CompaniesOrderItem, OrderLine } from "../../../../../company/reports/accounting/pos/posTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDinero } from "../../../../../currency/currencyLib"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { selectUserType } from "../../../../../login/loginSelectors"
import { useAppSelector } from "../../../../../store/storeHooks"
import { UserType } from "../../../../../login/loginTypes"

type useExportOrdersToCsvParams = Readonly<{
    data: CompaniesOrderItem[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportPosOrders({ data, startDate, endDate }: useExportOrdersToCsvParams) {
    const actualUserType = useAppSelector(selectUserType)
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-accounting-orders-${startDate}_${endDate}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapOrderToCsvRow = (order: CompaniesOrderItem, orderLine: OrderLine) => {
        return {
            "Transaction id": order?.id?.toString() ?? "",
            "Product name": orderLine?.name ?? "",
            Items: orderLine?.items?.toString() ?? "",
            [`Price (${getCurrencyString(orderLine.itemPrice)})`]: getAmountString(orderLine.itemPrice),
            [`Amount (${getCurrencyString(orderLine.price)})`]: getAmountString(orderLine.price),
            "Transaction Time": order?.created ?? "",
            "User id": order?.customer?.id?.toString() ?? "",
            ...(actualUserType !== UserType.SUPPLIER
                ? {
                      "Employee id": order?.customer?.employeeId ?? "",
                      Initials: order?.customer?.initials ?? "",
                      Surname: order?.customer?.surname ?? "",
                      GivenName: order?.customer?.givenName ?? "",
                      "E-mail": order?.customer?.email ?? "",
                  }
                : {}),
            Department: order?.customer?.department?.name ?? "",
            "User group id": order?.customer?.userGroup?.id?.toString() ?? "",
            "User group": order?.customer?.userGroup?.name ?? "",
            "Company VAT number": order?.accountingCustomerParty?.vatNumber ?? "",
            Company: order?.accountingCustomerParty?.name ?? "",
            "Payment method": order?.paymentDetails?.paymentMethod?.method
                ? l10n.getStringForEnum("purchases-payment-method", order.paymentDetails.paymentMethod.method, "")
                : "",
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapOrderToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.flatMap((order) =>
                order.orderLines.map((orderLine) => mapOrderToCsvRow(order, orderLine)),
            )
        } else {
            exportRows = [mapOrderToCsvRow({} as CompaniesOrderItem, {} as OrderLine)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n])
}
